/**
 * Hotel Business Verification Panel
 * Allows hotel owners to upload business documents for verification
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { verifications } from '../../lib/api';
import { fileToBase64, validateHotelDocument, formatFileSize } from '../../utils/fileUpload';
import { Building2, Upload, CheckCircle2, Clock, XCircle, Loader2, FileText, Info } from 'lucide-react';
import { toast } from 'sonner';

const DOCUMENT_TYPES = {
  business_registration: 'Business Registration',
  tax_permit: 'Tax Permit',
  business_license: 'Business License',
  owner_id: 'Owner ID'
};

export const HotelVerificationPanel = ({ hotelId, ownerEmail }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState({});

  useEffect(() => {
    fetchStatus();
  }, [hotelId]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await verifications.getHotelStatus(hotelId);
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching hotel verification status:', error);
      toast.error('Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (docType, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateHotelDocument(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = '';
      return;
    }

    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
  };

  const handleUpload = async () => {
    if (Object.keys(documents).length === 0) {
      toast.error('Please select at least one document to upload');
      return;
    }

    try {
      setUploading(true);
      
      // Convert all documents to base64
      const base64Docs = {};
      for (const [docType, file] of Object.entries(documents)) {
        base64Docs[docType] = await fileToBase64(file);
      }
      
      // Upload
      const response = await verifications.uploadHotelDocs({
        hotel_id: hotelId,
        owner_email: ownerEmail,
        documents: base64Docs
      });

      if (response.data.success) {
        toast.success('✅ Documents uploaded successfully! Admin will review within 24 hours.');
        setDocuments({});
        await fetchStatus();
      } else {
        toast.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;

    if (status.verified) {
      return (
        <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Verified
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
        <Clock className="w-3 h-3 mr-1" />
        Pending Review
      </Badge>
    );
  };

  if (loading && !status) {
    return (
      <Card data-testid="hotel-verification-panel">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            <CardTitle>Business Verification</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isVerified = status?.verified;
  const isPending = !isVerified && status?.verified_at;

  return (
    <Card data-testid="hotel-verification-panel" className="border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className={`w-5 h-5 ${
              isVerified ? 'text-emerald-500' : 'text-muted-foreground'
            }`} />
            <CardTitle>Business Verification</CardTitle>
          </div>
          {getStatusBadge()}
        </div>
        <CardDescription>
          {isVerified 
            ? 'Your business has been verified'
            : isPending
            ? 'Your documents are under review'
            : 'Verify your business to build trust with guests'
          }
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {isVerified ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <div>
                <p className="text-sm font-medium">Trust Score</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {status.owner_id_verified && (
                    <Badge variant="outline" className="text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Owner ID
                    </Badge>
                  )}
                  {status.tax_permit_verified && (
                    <Badge variant="outline" className="text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Tax Permit
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-2xl heading-font text-emerald-600">
                {status.trust_score || 90}/100
              </div>
            </div>

            <Alert data-testid="verification-success-info">
              <Info className="w-4 h-4" />
              <AlertDescription className="text-xs">
                Verified on {new Date(status.verified_at).toLocaleDateString()}. Your documents will be securely deleted after 7 days.
              </AlertDescription>
            </Alert>
          </div>
        ) : isPending ? (
          <Alert data-testid="verification-pending-info">
            <Clock className="w-4 h-4" />
            <AlertDescription className="text-xs">
              Your documents are under review by our admin team. You'll be notified within 24 hours.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            {/* Info */}
            <Alert data-testid="upload-info">
              <Info className="w-4 h-4" />
              <AlertDescription className="text-xs">
                Upload business registration, tax permits, and owner ID to get verified. Documents are encrypted and will be deleted after verification.
              </AlertDescription>
            </Alert>

            {/* Document Upload Fields */}
            <div className="space-y-4">
              {Object.entries(DOCUMENT_TYPES).map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label htmlFor={`doc-${key}`} className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {label}
                    {key === 'business_registration' && (
                      <Badge variant="secondary" className="text-xs">Required</Badge>
                    )}
                  </Label>
                  <Input
                    id={`doc-${key}`}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                    onChange={(e) => handleFileSelect(key, e)}
                    disabled={uploading}
                    data-testid={`hotel-doc-${key}-input`}
                  />
                  {documents[key] && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      {documents[key].name} ({formatFileSize(documents[key].size)})
                    </p>
                  )}
                </div>
              ))}
            </div>

            <Separator />

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={Object.keys(documents).length === 0 || uploading}
              className="w-full"
              data-testid="upload-hotel-docs-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Documents ({Object.keys(documents).length})
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};